// Zero-dependency error type for the API layer.
//
// Deliberately its own leaf module (imported by `api.ts`, `auth/authErrors.ts`,
// and ~40 call sites) so that `auth/authErrors.ts` can `instanceof`-check it
// WITHOUT importing `api.ts` — which pulls in `auth/keycloak.ts` and would
// otherwise form an `authErrors → api → keycloak → authErrors` import cycle
// once `keycloak.ts` reuses `isTransientAuthError`. `api.ts` re-exports both
// symbols, so existing `import { ApiRequestError, type ApiError } from '.../api'`
// call sites keep resolving unchanged.

export type ApiError = {
  message?: string;
  details?: string[] | string;
};

export class ApiRequestError extends Error {
  readonly status: number;
  readonly details?: string[] | string;
  readonly payload?: unknown;
  /**
   * Seconds the server asked the client to wait, from a `Retry-After`
   * response header. Undefined when the header is absent or unreadable
   * (a cross-origin response must list it in Access-Control-Expose-Headers).
   */
  readonly retryAfterSeconds?: number;

  constructor(
    message: string,
    status: number,
    details?: string[] | string,
    payload?: unknown,
    retryAfterSeconds?: number,
  ) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.details = details;
    this.payload = payload;
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

/**
 * Parses a `Retry-After` header value: either delay-seconds or an HTTP-date.
 * Returns undefined for an absent or malformed value, never a negative delay.
 */
export const parseRetryAfterSeconds = (value: string | null, now: number = Date.now()): number | undefined => {
  if (value === null) return undefined;
  const trimmed = value.trim();
  if (/^\d+$/.test(trimmed)) return Number(trimmed);
  // An HTTP-date names its day and month; bare numbers such as "-5" are not
  // dates even though Date.parse would accept them.
  if (!/^[A-Za-z]{3}, /.test(trimmed)) return undefined;
  const date = Date.parse(trimmed);
  if (!Number.isFinite(date)) return undefined;
  return Math.max(0, Math.ceil((date - now) / 1000));
};
